import React, {useEffect, useMemo, useRef, useState} from "react";
import{ useSelector, useDispatch} from "react-redux";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import {EmployeeTimeSheetHelper} from "../../../src/screens/AttendenceScreen/EmployeeTimeSheetHelper";


import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Clock3,
    Download,
    Timer,
    CheckCircle2,
    MoreVertical,
    User,
    MessageCircleMore,
    LucideAlertTriangle,
    LucideCirclePlus, ChevronDown, Trash2Icon,
} from "lucide-react";

import NavigationSideBar from "../../components/Layout/Graph & Tables/NavigationSideBar";
import DashboardHeader from "../../components/Layout/DashBoardHeader";


import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {useNavigate, useParams} from "react-router-dom";
import {useFetchAllUserHoursQuery,
        useCreateEmployeeHoursMutation,
        useUpdateEmployeeHoursWorkedMutation,
        useDeleteEmployeeHoursMutation}
    from "../../../src/features/attendanceApiSlice";
import Exceljs from "exceljs";
import {toast} from "react-toastify";
import AttendanceChatWindow from "../../../src/screens/mainscreen/AttendanceChatWindow";
import {useListAllProjectEmployeesQuery} from "../../../src/features/projectApiSlice";

// ============================================================
// Synthetic Data
// ============================================================

const employee = {
    department: "Field Operations",
    position: "Field Supervisor",
};

export default function EmployeeWeeklyTimesheetScreen() {

    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const [weekOffset, setWeekOffset] = useState<number>(0);

    const {userInfo} = useSelector((state: any) => state).auth;

    const [isAttendanceChatOpen, setIsAttendanceChatOpen] = useState(false);

    const userId = userInfo?.id;

    const navigate = useNavigate();

    // @ts-ignore
    const {data: assignedEmployees, isLoading: assignedEmployeesLoading, refetch:refetchAssignedEmployees} = useListAllProjectEmployeesQuery();

    const {id}= useParams();

    const [selectedUserId, setSelectedUserId] = useState<string>(userInfo?.id ?? "");

    const projectAssignedEmployees = useMemo(() => {
        if (!Array.isArray(assignedEmployees) || !id) {
            return [];
        }

        // Only fetch employees assigned to this project
        const employees = assignedEmployees.filter((item: any) => item.projectId == id);

        return Array.from(
            new Map(
                employees.map((item:any) => [
                    item.userId,
                    item
                ])
            ).values()
        );

    }, [assignedEmployees, id]);

    useEffect(() => {
        if (projectAssignedEmployees.length === 0) {
            setSelectedUserId("");
            return;
        }

        const currentEmployeeExists = projectAssignedEmployees.some(
            (employee: any) => employee.userId === selectedUserId
        );

        if (!currentEmployeeExists) {
            setSelectedUserId(projectAssignedEmployees[0].userId);
        }
    }, [projectAssignedEmployees]);


    const {data: workHours, isLoading, refetch} = useFetchAllUserHoursQuery({
        userId: selectedUserId,
        projectId: id,
    },
        {
            skip:!selectedUserId || !id,
        }
        );


    const [updateHours, {isLoading: isUpdateHours}] = useUpdateEmployeeHoursWorkedMutation();
    const [createNewHours, {isLoading: isCreateHoursLoading}] = useCreateEmployeeHoursMutation();
    const [deleteHours] = useDeleteEmployeeHoursMutation();

    const [signInTime, setSignInTime] = useState<string>("");
    const [signOutTime, setSignOutTime] = useState<string>("");

    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [openHoursEditor, setOpenHoursEditor] = useState<boolean>(false);
    const [editingSession, setEditingSession] = useState<any>(null);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [openCreateHours, setOpenCreateHours] = useState<boolean>(false);
    const [openSessionId, setOpenSessionId] = useState<string | null>(null);
    const [openSession, setOpenSession] = useState<any>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toDateTimeLocal = (value?: string | null) => {
        if (!value) return "";

        const date = new Date(value);

        const offset = date.getTimezoneOffset() * 60_000;

        return new Date(date.getTime() - offset)
            .toISOString()
            .slice(0, 16);
    };

    // --------------------------------------------------------
    // Calculate summary
    // --------------------------------------------------------

    const STANDARD_WEEKLY_HOURS = 40;

    // Find Work Week
    const getWeekRange = (date: Date) => {
        const current = new Date(date);

        const day = current.getDay();

        // Monday = first day of week
        const mondayOffset = day === 0 ? -6 : 1 - day;

        const weekStart = new Date(current);

        weekStart.setDate(
            current.getDate() + mondayOffset
        );

        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);

        weekEnd.setDate(
            weekStart.getDate() + 6
        );

        weekEnd.setHours(23, 59, 59, 999);

        return {
            weekStart,
            weekEnd,
        };
    };

    const {
        weekStart,
        weekEnd,
    } = useMemo(() => {
        const date = new Date();

        date.setDate(
            date.getDate() + weekOffset * 7
        );

        return getWeekRange(date);
    }, [weekOffset]);

    const myWeeklyHours = useMemo(() => {
        if (!Array.isArray(workHours)) {
            return [];
        }

        return workHours.filter((item: any) => {
            if (!item.signInTime) {
                return false;
            }

            const signInDate = new Date(item.signInTime);

            return (
                signInDate >= weekStart &&
                signInDate <= weekEnd
            );
        });
    }, [
        workHours,
        weekStart,
        weekEnd,
    ]);

    // Figure out working days
    const workingDays = useMemo(() => {
        const uniqueDays = new Set(
            myWeeklyHours
                .filter(
                    (item: any) =>
                        item.status === "Completed" &&
                        Number(item.totalHours ?? 0) > 0
                )
                .map((item: any) =>
                    new Date(item.signInTime)
                        .toISOString()
                        .split("T")[0]
                )
        );

        return uniqueDays.size;
    }, [myWeeklyHours]);

    const weeklyTableDays = useMemo(() => {
        return Array.from({ length: 7 }, (_, index) => {
            const date = new Date(weekStart);

            date.setDate(weekStart.getDate() + index);

            return {
                date,
                dateKey: date.toISOString().split("T")[0],
                dayName: date.toLocaleDateString("en-US", {
                    weekday: "long",
                }),
            };
        });
    }, [weekStart]);

    const weeklyTableRows = useMemo(() => {
        return weeklyTableDays.map((day) => {
            const sessionsForDay = myWeeklyHours.filter((item: any) => {
                if (!item.signInTime) return false;

                const itemDateKey = new Date(item.signInTime)
                    .toISOString()
                    .split("T")[0];

                return itemDateKey === day.dateKey;
            });

            const firstSession = sessionsForDay[0];

            const totalHours = sessionsForDay.reduce(
                (total: number, item: any) =>
                    total + Number(item.totalHours ?? 0),
                0
            );

            const exceedsDailyLimit = totalHours > 12;

            const signInTime =
                sessionsForDay.length > 0
                    ? sessionsForDay.reduce(
                        (earliest: string, item: any) =>
                            new Date(item.signInTime) <
                            new Date(earliest)
                                ? item.signInTime
                                : earliest,
                        sessionsForDay[0].signInTime
                    )
                    : null;

            const signOutTime =
                sessionsForDay.length > 0
                    ? sessionsForDay.reduce(
                        (latest: string | null, item: any) => {
                            if (!item.signOutTime) {
                                return latest;
                            }

                            if (!latest) {
                                return item.signOutTime;
                            }

                            return new Date(item.signOutTime) >
                            new Date(latest)
                                ? item.signOutTime
                                : latest;
                        },
                        null
                    )
                    : null;

            return {
                id: day.dateKey,
                date: day.dateKey,
                day: day.dayName,

                projectName: firstSession?.projectName ?? null,
                projectNumber: firstSession?.projectNumber ?? null,
                projectId: firstSession?.projectId ?? null,
                siteCode: firstSession?.siteCode ?? null,
                deviceId: firstSession?.deviceId ?? null,

                signInTime,
                signOutTime,
                totalHours,

                exceedsDailyLimit,

                status:
                    totalHours > 0
                        ? "Completed"
                        : "No Work"
            };
        });
    }, [weeklyTableDays, myWeeklyHours]);

    const totalWeeklyHours = useMemo(() => {
        return myWeeklyHours.reduce(
            (total: number, item: any) =>
                total + Number(item.totalHours ?? 0),
            0
        );
    }, [myWeeklyHours]);

    const formatDisplayDate = (dateString: string) => {
        const [year, month, day] = dateString.split("-").map(Number);

        return new Date(year, month - 1, day).toLocaleDateString(
            "en-US",
            {
                month: "short",
                day: "numeric",
            }
        );
    };

    const regularHours = Math.min(
        totalWeeklyHours,
        STANDARD_WEEKLY_HOURS
    );

    const overtimeHours = Math.max(
        totalWeeklyHours - STANDARD_WEEKLY_HOURS,
        0
    );

    const formatHours = (decimalHours: number) => {
        const totalMinutes = Math.round(
            decimalHours * 60
        );

        const hours = Math.floor(
            totalMinutes / 60
        );

        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
        });
    };

    const handleExcelExport = async () => {
        const workbook = new Exceljs.Workbook();

        workbook.creator = "K Solution";
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet("Daily Attendance");

        // Get the selected employee
        const selectedEmployee = projectAssignedEmployees.find(
            (employee: any) => employee.userId === selectedUserId
        );

        const userName = selectedEmployee?.userName ?? "Unknown Employee";
        const employeeNumber = selectedEmployee?.employeeNumber ?? "";

        // Employee information
        worksheet.mergeCells("A1:J1");
        worksheet.getCell("A1").value = "Daily Attendance Report";
        worksheet.getCell("A1").font = {
            bold: true,
            size: 16,
        };

        worksheet.mergeCells("A2:B2");
        worksheet.getCell("A2").value = `Employee Name: ${userName}`;
        worksheet.getCell("A2").font = { bold: true, size: 12 };

        worksheet.mergeCells("C2:D2");
        worksheet.getCell("C2").value = `Employee ID: ${employeeNumber}`;
        worksheet.getCell("C2").font = { bold: true, size: 12 };

        const columns = [
            { header: "Date", key: "date", width: 40 },
            { header: "Day", key: "day", width: 40 },
            { header: "Project Name", key: "projectName", width: 40 },
            { header: "Project Number", key: "projectNumber", width: 40 },
            { header: "Site Code", key: "siteCode", width: 40 },
            { header: "Device ID", key: "deviceId", width: 40 },
            { header: "Sign In", key: "signInTime", width: 40 },
            { header: "Sign Out", key: "signOutTime", width: 40 },
            { header: "Total Hours", key: "totalHours", width: 40 },
            { header: "Status", key: "status", width: 40 },
        ];

        worksheet.addTable({
            name: "DailyAttendanceTable",
            ref: "A4",
            headerRow: true,
            style: {
                theme: "TableStyleMedium2",
                showRowStripes: true,
            },
            columns: columns.map(({ header }) => ({
                name: header,
                filterButton: true,
            })),
            rows: weeklyTableRows.map((day) => [
                day.date,
                day.day,
                day.projectName ?? "",
                day.projectNumber ?? "",
                day.siteCode ?? "",
                day.deviceId ?? "",
                day.signInTime
                    ? new Date(day.signInTime).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    })
                    : "",
                day.signOutTime
                    ? new Date(day.signOutTime).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    })
                    : "",
                Number(day.totalHours.toFixed(2)),
                day.status,
            ]),
        });

        // Format date and hours columns
        worksheet.getColumn(1).numFmt = "yyyy-mm-dd";
        worksheet.getColumn(9).numFmt = "0.00";

        // Freeze the table header
        worksheet.views = [{ state: "frozen", ySplit: 4 }];

        // Download Excel file
        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        const formatFilenameDate = (date: Date) =>
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        const safeUserName = userName.replace(/[^a-zA-Z0-9_-]/g, "_");

        link.href = url;
        link.download =
            `${safeUserName}_DailyAttendance_${formatFilenameDate(weekStart)}_to_${formatFilenameDate(weekEnd)}.xlsx`;

        link.click();
        URL.revokeObjectURL(url);
    };

    // --------------------------------------------------------
    // Week navigation
    // --------------------------------------------------------

    const handlePreviousWeek = () => {
        setWeekOffset((prev) => prev - 1);
    };

    const handleNextWeek = () => {
        setWeekOffset((prev) => prev + 1);
    };

    const handleCurrentWeek = () => {
        setWeekOffset(0);
    };

    // --------------------------------------------------------
    // Edit Project
    // --------------------------------------------------------

    const onSelectHoursEdit = (session: any) => {
        console.log("Selected session:", session);

        const sessionId = session?.id;

        if (!sessionId) {
            toast.error("Work session ID is missing from the API response.");
            return;
        }


        setEditingSessionId(session.id);
        setEditingSession(session);

        setSignInTime(toDateTimeLocal(session.signInTime));
        setSignOutTime(toDateTimeLocal(session.signOutTime));

        setOpenHoursEditor(true);

    }

    const onCreateHoursHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userInfo) {
            toast.error("You must be an administrator to edit this project now!");
            return;
        }

        if (!signInTime || !signOutTime) {
            toast.error("Please enter both clock-in and clock-out times.");
            return;
        }

        const signIn = new Date(signInTime).toISOString();
        const signOut = new Date(signOutTime).toISOString();
        const newSignInTime = new Date(signInTime);
        const newSignOutTime = new Date(signOutTime);


        if (signOut <= signIn) {
            toast.error("Clock-out must be later than clock-in");
            return;
        }

        // Prevent duplicate sessions with the same time
        const duplicateSession = workHours?.some((session) => {
            return (
                    session.userId === userInfo.id &&
                        session.projectId === id &&
                       new Date(session.signInTime).getTime() === newSignInTime.getTime() &&
                        session.signOutTime &&
                        new Date(session.signOutTime).getTime() === newSignOutTime.getTime()
            );
        });

        if (duplicateSession) {
            toast.error("These work hours have been recorded");
        }

        try {
            await createNewHours({
                signInTime: signIn,
                signOutTime: signOut,
                userId: selectedUserId,
                projectId: id,
            }).unwrap();

            toast.success("Employee hours updated successfully.");

            setOpenCreateHours(false);
            setOpenSessionId(null);
            setOpenSession(null);
            setSignInTime("");
            setSignOutTime("");

            await refetch();

        } catch (error: any) {
            console.error("Updating employee hours failed:", error);

            toast.error(
                error.message,

            )
        }
    }

    // Delete Work Hours
    const onSelectWorkHourSessionDelete = async (sessionId: string) => {

        if (!sessionId) {
            toast.error("Work sesssion Id is missing")
            return;
        }

        if (window.confirm("This will permanently delete the project")) {

            try {
                await deleteHours(sessionId).unwrap();

                await refetch();

                toast.success("Project deleted successfully.");

            } catch (error) {
                toast.error("Problem with deleting this project now!");
            }
        }
        setActiveMenu(null);
    }


    const onEditTimeHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userInfo) {
            toast.error("You must be an administrator to edit this project now!");
            return;
        }

        if (!editingSessionId) {
            toast.error("Working session ID is missing");
        }

        if (!signInTime || !signOutTime) {
            toast.error("Please enter both clock-in and clock-out times.");
            return;
        }

        const signIn = new Date(signInTime).toISOString();
        const signOut = new Date(signOutTime).toISOString();

        if (signOut <= signIn) {
            toast.error("Clock-out must be later than clock-in");
            return;
        }

        try {
            await updateHours({
                sessionId: editingSessionId,
                signInTime: signIn,
                signOutTime: signOut,
                userName: userInfo.userName,
                userId: userInfo.id,
                projectId: id,
            }).unwrap();

            toast.success("Employee hours updated successfully.");

            setOpenHoursEditor(false);
            setEditingSessionId(null);
            setEditingSession(null);
           
            await refetch();

        } catch (error: any) {
            console.error("Updating employee hours failed:", error);

            toast.error(
                error.message,

            )
        }
    }

    // when user click outside the ellipsis, the window closes
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setActiveMenu(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        refetch()
    }, []);
    // --------------------------------------------------------
    // Selected day
    // --------------------------------------------------------

    const selectedDay = weeklyTableRows.find(
        (day) => day.date === selectedDate
    );

    const getSessionsForDay = (date: string) =>
        myWeeklyHours.filter((session: any) => {
            if (!session.signInTime) return false;

            return (
                new Date(session.signInTime).toISOString().slice(0, 10) === date
            );
        });

    return (
        <main className="min-h-dvh">

            <Helmet>
                <title>
                    Weekly Timesheet
                </title>

                <meta
                    name="description"
                    content="Employee weekly timesheet"
                />
            </Helmet>

            { isLoading ? (
                <CustomLoaderSmall />
            ) : (
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 20,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        delay: 0.2,
                    }}
                    className="
                    min-h-dvh
                    bg-[#0A0A0A]
                    text-white
                    flex
                "
                >
                    <div className="
                    flex
                    min-h-dvh
                ">
                        {/* ==================================================
                        SIDEBAR
                        Desktop only.
                    ================================================== */}

                        <div className="
                        hidden
                        lg:block
                    ">
                            <NavigationSideBar />
                        </div>

                        {/* ==================================================
                        MAIN
                    ================================================== */}

                        <div className="
                        flex
                        flex-1
                        min-w-0
                        flex-col
                    ">
                            {/* HEADER */}
                            <DashboardHeader projectId={id} />

                            {/* ==================================================
                            CONTENT CONTAINER

                            iPhone:
                            px-4

                            iPad:
                            px-6 / px-8

                            Desktop:
                            px-10
                        ================================================== */}

                            <main className="
                            w-full
                            max-w-[1600px]
                            mx-auto

                            px-4
                            sm:px-6
                            md:px-8
                            lg:px-10

                            py-5
                            sm:py-6
                            md:py-8

                            pb-[env(safe-area-inset-bottom)]
                        ">


                                {/* ==================================================
                                PAGE HEADER
                            ================================================== */}

                                <section className="
                                flex
                                flex-col
                                lg:flex-row
                                lg:items-center
                                lg:justify-between
                                gap-4
                                mb-6
                            ">

                                    <div className="
                                    flex
                                    items-center
                                    gap-3
                                ">


                                        <div className="
                                        flex
                                        h-11
                                        w-11
                                        shrink-0
                                        items-center
                                        justify-center

                                        rounded-full

                                        bg-blue-500/10
                                        border
                                        border-blue-500/20
                                    ">

                                            <User
                                                size={23}
                                                className="
                                                text-blue-400
                                            "
                                            />

                                        </div>


                                        <div className="
                                        min-w-0
                                    ">

                                            <h1 className="
                                            text-xl
                                            sm:text-2xl
                                            font-bold
                                            text-white
                                        ">
                                                Weekly Timesheet
                                            </h1>

                                            <div className="mt-3 w-full max-w-sm">

                                                <label
                                                    htmlFor="employeeSelect"
                                                    className="mb-2 block text-xs font-medium text-gray-400"
                                                >
                                                    Select Employee
                                                </label>

                                                <div className="relative">

                                                    <select
                                                        id="employeeSelect"
                                                        value={selectedUserId}
                                                        onChange={(e) => {
                                                            setSelectedUserId(e.target.value);
                                                            setSelectedDate(null);
                                                            setWeekOffset(0);
                                                        }}
                                                        disabled={projectAssignedEmployees.length === 0}
                                                        className="
                w-full
                appearance-none
                rounded-xl
                border border-gray-700
                bg-[#181818]
                px-4
                py-3
                pr-10
                text-sm
                font-medium
                text-white
                outline-none
                transition
                focus:border-blue-500
                disabled:cursor-not-allowed
                disabled:opacity-50
            "
                                                    >
                                                        {projectAssignedEmployees.length === 0 ? (
                                                            <option value="">
                                                                No employees assigned
                                                            </option>
                                                        ) : (
                                                            projectAssignedEmployees.map((employee: any) => (
                                                                <option
                                                                    key={employee.userId}
                                                                    value={employee.userId}
                                                                >
                                                                    {employee.userName}
                                                                </option>
                                                            ))
                                                        )}
                                                    </select>

                                                    <ChevronDown
                                                        size={18}
                                                        className="
                pointer-events-none
                absolute
                right-3
                top-1/2
                -translate-y-1/2
                text-gray-400
            "
                                                    />

                                                </div>
                                            </div>


                                            <p className="
                                            mt-1
                                            truncate
                                            text-xs
                                            sm:text-sm
                                            text-gray-400
                                        ">

                                                {projectAssignedEmployees.find((employee: any) => employee.userId === selectedUserId)?.userName ?? "Select an employee"}

                                                <span className="
                                                mx-1
                                                sm:mx-2
                                            ">
                                                •
                                            </span>

                                                {userInfo?.employeeNumber}

                                            </p>

                                        </div>

                                    </div>

                                    {/* EXPORT */}
                                    <div className={"justify-center flex flex-row gap-4"}>
                                        <button type={"button"}
                                                onClick={() => setIsAttendanceChatOpen(true)}
                                                aria-label={"Open attendance assistant"}
                                                title={"Ask the attendance assistant"}
                                                className={"flex items-center justify-center gap-3 rounded-xl borer border-gray-700" +
                                                    "px-3 py-2 text-blue-400 transition hover:bg-gray-800 hover:text-blue-300 p-2"}>
                                            <MessageCircleMore size={28} />
                                                <span className={"hidden sm:inline text-sm"}>
                                                    Chat
                                                </span>

                                        </button>

                                        <button
                                            onClick={() =>
                                                handleExcelExport()
                                            }
                                            className="
                                        w-full
                                        md:w-auto
                                        min-h-[48px]
                                        flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-xl
                                        bg-blue-600
                                        hover:bg-blue-500
                                        px-5
                                        text-sm
                                        font-semibold
                                        transition
                                    "
                                        >
                                            <Download
                                                size={17}
                                            />

                                            Export Timesheet

                                        </button>
                                    </div>



                                </section>


                                {/* ==================================================
                                EMPLOYEE INFO
                            ================================================== */}

                                <section className="
                                mb-5
                                rounded-xl
                                border
                                border-gray-800
                                bg-[#101010]
                                p-4
                                md:p-5
                            ">

                                    <div className="
                                    grid
                                    grid-cols-1
                                    sm:grid-cols-2
                                    md:grid-cols-3

                                    gap-4
                                ">

                                        <EmployeeTimeSheetHelper.InfoItem
                                            label="Department"
                                            value={
                                                employee.department
                                            }
                                        />


                                        <EmployeeTimeSheetHelper.InfoItem
                                            label="Position"
                                            value={
                                                employee.position
                                            }
                                        />


                                        <EmployeeTimeSheetHelper.InfoItem
                                            label="Working Days"
                                            value={`${workingDays} days`}
                                        />

                                    </div>

                                </section>


                                {/* ==================================================
                                SUMMARY CARDS

                                iPhone:
                                2 columns

                                iPad:
                                4 columns
                            ================================================== */}

                                <section className="
                                grid
                                grid-cols-2
                                md:grid-cols-4
                                gap-3
                                md:gap-4
                                mb-5
                            ">
                                    <EmployeeTimeSheetHelper.ResponsiveSummaryCard
                                        icon={Clock3}
                                        label="Regular"
                                        value={formatHours(
                                            regularHours
                                        )}
                                    />
                                    <EmployeeTimeSheetHelper.ResponsiveSummaryCard
                                        icon={Timer}
                                        label="Overtime"
                                        value={formatHours(
                                            overtimeHours
                                        )}
                                    />
                                    <EmployeeTimeSheetHelper.ResponsiveSummaryCard
                                        icon={CalendarDays}
                                        label="Total"
                                        value={formatHours(
                                            totalWeeklyHours
                                        )}
                                    />
                                    <EmployeeTimeSheetHelper.ResponsiveSummaryCard
                                        icon={CheckCircle2}
                                        label="Status"
                                        value="Submitted"
                                    />
                                </section>

                                {/* ==================================================
                                WEEK NAVIGATION
                            ================================================== */}

                                <section className="
                                mb-5
                                rounded-xl
                                border
                                border-gray-800
                                bg-[#101010]
                                p-3
                                sm:p-4
                            ">
                                    <div className="
                                    grid
                                    grid-cols-[48px_1fr_48px]
                                    items-center
                                    gap-2
                                ">
                                        {/* PREVIOUS */}
                                        <button
                                            onClick={
                                                handlePreviousWeek
                                            }
                                            aria-label="
                                            Previous week
                                        "
                                            className="
                                            flex
                                            h-11
                                            w-11
                                            items-center
                                            justify-center

                                            rounded-xl

                                            bg-gray-800
                                            hover:bg-gray-700
                                        "
                                        >
                                            <ChevronLeft
                                                size={20}
                                            />

                                        </button>

                                        {/* DATE */}
                                        <div className="
                                        text-center
                                        min-w-0
                                    ">
                                            <p className="
                                            text-sm
                                            sm:text-base
                                            font-semibold
                                            text-white
                                        ">
                                                {formatDate(
                                                    weekStart
                                                )}

                                                {" – "}

                                                {formatDate(
                                                    weekEnd
                                                )}
                                            </p>

                                            <p className="
                                            mt-1
                                            text-[10px]
                                            sm:text-xs
                                            text-gray-500
                                            uppercase
                                            tracking-wide
                                        ">
                                                Weekly Ending
                                            </p>

                                            {weekOffset !== 0 && (

                                                <button
                                                    onClick={
                                                        handleCurrentWeek
                                                    }
                                                    className="
                                                    mt-1
                                                    text-xs
                                                    text-blue-400
                                                "
                                                >
                                                    Return To Current Week
                                                </button>
                                            )}
                                        </div>

                                        {/* NEXT */}
                                        <button
                                            onClick={
                                                handleNextWeek
                                            }
                                            aria-label="
                                            Next week
                                        "
                                            className="
                                            flex
                                            h-11
                                            w-11
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-gray-800
                                            hover:bg-gray-700
                                        "
                                        >
                                            <ChevronRight
                                                size={20}
                                            />
                                        </button>
                                    </div>
                                </section>

                                {/* ==================================================
                                MOBILE ATTENDANCE

                                <= 767px

                                We don't use the large table
                                on iPhone.
                            ================================================== */}
                                <section className="
                                md:hidden
                            ">
                                    <div className="
                                    mb-3
                                    flex
                                    items-center
                                    justify-between
                                ">
                                        <div>
                                            <h2 className="
                                            text-lg
                                            font-semibold
                                        ">
                                                Weekly Attendance
                                            </h2>

                                            <p className="
                                            text-xs
                                            text-gray-500
                                        ">
                                                Daily work sessions
                                            </p>
                                        </div>

                                        <span className="
                                        rounded-full
                                        border
                                        border-blue-500/20
                                        bg-blue-500/10
                                        px-2.5
                                        py-1
                                        text-[10px]
                                        text-blue-400
                                    ">
                                        {workingDays} days
                                    </span>

                                    </div>

                                    <div className="
                                    space-y-3
                                ">

                                        {weeklyTableRows.map(
                                            (day) => (

                                                <EmployeeTimeSheetHelper.MobileDayCard
                                                    key={day.id}
                                                    day={day}
                                                    onSelect={() =>
                                                        day.projectName &&
                                                        setSelectedDate(
                                                            day.date
                                                        )
                                                    }
                                                />

                                            )
                                        )}
                                    </div>
                                </section>
                                {/* ==================================================
                                TABLET / DESKTOP TABLE

                                >= 768px
                            ================================================== */}

                                <section className="
                                hidden
                                md:block
                                rounded-xl
                                border
                                border-gray-800
                                bg-[#101010]
                                overflow-hidden
                            ">
                                    <div className="
                                    flex
                                    items-center
                                    justify-between
                                    gap-3
                                    border-b
                                    border-gray-800
                                    p-4
                                ">
                                        <div>
                                            <h2 className="
                                            text-lg
                                            font-semibold
                                        ">
                                                Weekly Attendance
                                            </h2>

                                            <p className="
                                            mt-1
                                            text-xs
                                            text-gray-500
                                        ">
                                                Daily Employee Work Sessions
                                            </p>

                                        </div>

                                        <div className={"flex flex-row items-center gap-3"}>
                                              <span className="
                                        shrink-0

                                        rounded-full
                                        border
                                        border-blue-500/20
                                        bg-blue-500/10
                                        px-3
                                        py-1
                                        text-xs
                                        text-blue-400
                                    ">
                                        {workingDays} Working Days
                                    </span>

                                            <button
                                                type={"button"}
                                                onClick={() => {
                                                    setEditingSessionId(null);
                                                    setEditingSession(null);
                                                    setSignInTime("");
                                                    setSignOutTime("");
                                                    setOpenCreateHours(true);
                                                }}
                                            className={"inline-flex min-h-9 items-center gap- rounded-lg bg-blue-600 px-2 py-1 " +
                                                "text-sm font-semibold text-white hover:bg-blue-500"}
                                            >
                                                <LucideCirclePlus size={18 } />
                                                Add Hours
                                            </button>
                                        </div>


                                    </div>

                                    {/* TABLE */}
                                    <div className="
                                    overflow-x-auto
                                ">
                                        <table className="
                                        w-full
                                        min-w-[760px]
                                        text-left
                                    ">
                                            <thead className="
                                            bg-gray-900/50
                                        ">
                                            <tr>
                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Date
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Project
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Sign In
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Sign Out
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Total
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Status
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <EmployeeTimeSheetHelper.TableHeader>
                                                    Configure
                                                </EmployeeTimeSheetHelper.TableHeader>

                                                <th className="p-3" />
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {weeklyTableRows.map(
                                                (day) => (

                                                    <tr
                                                        key={day.id}
                                                        onClick={() =>
                                                            day.projectName &&
                                                            setSelectedDate(
                                                                day.date
                                                            )
                                                        }

                                                        className="
                                                            border-t
                                                            border-gray-800
                                                            transition
                                                            hover:bg-gray-900/60
                                                        "
                                                    >
                                                        <td className="p-3">
                                                            <p className="
                                                                text-sm
                                                                font-medium
                                                            ">
                                                                {day.day}
                                                            </p>
                                                            <p className="
                                                                mt-1
                                                                text-xs
                                                                text-gray-500
                                                            ">
                                                                {formatDisplayDate(
                                                                    day.date
                                                                )}
                                                            </p>
                                                        </td>

                                                        <td className="
                                                            max-w-[180px]
                                                            p-3
                                                        ">

                                                            <p className="
                                                                truncate
                                                                text-sm
                                                                text-gray-200
                                                            ">
                                                                {day.projectName ??
                                                                    "—"}
                                                            </p>

                                                            <p className="
                                                                mt-1
                                                                text-[11px]
                                                                text-gray-500
                                                            ">
                                                                {day.projectNumber}
                                                            </p>
                                                        </td>

                                                        <td className="
                                                            p-3
                                                            text-sm
                                                            text-gray-300
                                                        ">

                                                            {day.signInTime
                                                                ? new Date(
                                                                    day.signInTime
                                                                ).toLocaleTimeString(
                                                                    "en-US",
                                                                    {
                                                                        timeStyle:
                                                                            "short",
                                                                    }
                                                                )
                                                                : "—"}
                                                        </td>

                                                        <td className="
                                                            p-3
                                                            text-sm
                                                            text-gray-300
                                                        ">

                                                            {day.signOutTime
                                                                ? new Date(
                                                                    day.signOutTime
                                                                ).toLocaleTimeString(
                                                                    "en-US",
                                                                    {
                                                                        timeStyle:
                                                                            "short",
                                                                    }
                                                                )
                                                                : "—"}
                                                        </td>

                                                        <td className={"p-3"}>
                                                            <div className={"flex items-center gap-2"}>
                                                                <span className={`text-sm font-semibold ${day.exceedsDailyLimit
                                                                ? "text-red-500"
                                                                : "text-gray-500"}`}>
                                                                    {day.totalHours.toFixed(2)} hrs
                                                                </span>

                                                                {day.exceedsDailyLimit && (
                                                                    <span
                                                                        title={"Daily work hours exceeds 12 hours"}
                                                                        className={"inline-flex items-center gap-1 rounded-full border border-red-500 bg-red-500/10 px-2 py-1 " +
                                                                        "text-sm font-medium text-red-500"}>

                                                                        <LucideAlertTriangle size={16} />
                                                                        Warning
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {day.exceedsDailyLimit && (
                                                                <p className={"mt-1 text-sm text-red-500"}>
                                                                    Exceeds limit by{" "}
                                                                    {(day.totalHours - 12).toFixed(2)} hrs
                                                                </p>
                                                            )}
                                                        </td>



                                                        <td className="
                                                            p-3
                                                       ">
                                                            <EmployeeTimeSheetHelper.StatusBadge
                                                                status={
                                                                    day.status
                                                                }
                                                            />
                                                        </td>


                                                        <td className="p-3">
                                                            <div className="relative flex justify-center">
                                                                {getSessionsForDay(day.date).map((session: any) => (
                                                                    <div key={session.id} className="relative">
                                                                        <button
                                                                            type="button"
                                                                            aria-label="Open work session actions"
                                                                            aria-expanded={activeMenu === session.id}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();

                                                                                setActiveMenu((current) =>
                                                                                    current === session.id ? null : session.id
                                                                                );
                                                                            }}
                                                                            className="rounded-lg p-2 text-gray-300 hover:bg-gray-800"
                                                                        >
                                                                            <MoreVertical size={20} />
                                                                        </button>

                                                                        {activeMenu === session.id && (
                                                                            <div
                                                                                className="absolute right-0 top-full z-50 mt-1 w-44
                                                                                           rounded-xl border border-gray-700
                                                                                           bg-[#181818] p-1 shadow-xl"
                                                                                onClick={(event) => event.stopPropagation()}
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => onSelectHoursEdit(session)}
                                                                                    className="flex w-full items-center gap-3 rounded-lg
                                                                                     px-3 py-2 text-left text-sm text-gray-200
                                                                                     hover:bg-gray-800"
                                                                                >
                                                                                    <Clock3 size={16} />
                                                                                    Edit Hours
                                                                                </button>

                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        onSelectWorkHourSessionDelete(session.id)
                                                                                    }
                                                                                    className="flex w-full items-center gap-3 rounded-lg
                                                                                                 px-3 py-2 text-left text-sm text-red-400
                                                                                                 hover:bg-red-500/10"
                                                                                >
                                                                                    <Trash2Icon size={16} />
                                                                                    Delete Session
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>


                                                    </tr>
                                                )
                                            )}
                                            </tbody>

                                            <tbody>
                                            {myWeeklyHours.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={7}
                                                        className="px-4 py-12 text-center"
                                                    >
                                                        <div className="flex flex-col items-center gap-3">
                                                            <Clock3
                                                                size={32}
                                                                className="text-gray-500"
                                                            />

                                                            <p className="font-medium text-white">
                                                                No hours recorded for this week
                                                            </p>

                                                            <p className="text-sm text-gray-400">
                                                                Add a sign-in and sign-out time to create
                                                                your first work session.
                                                            </p>

                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSignInTime("");
                                                                    setSignOutTime("");
                                                                    setEditingSessionId(null);
                                                                    setEditingSession(null);
                                                                    setOpenCreateHours(true);
                                                                }}
                                                                className="mt-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-500"
                                                            >
                                                                Add Hours
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                weeklyTableRows.map((day) => (
                                                    // Keep your existing <tr> for each day here.
                                                    <tr key={day.id}>
                                                        {/* Your existing table cells */}
                                                    </tr>
                                                ))
                                            )}
                                            </tbody>


                                        </table>
                                    </div>
                                </section>

                                {openHoursEditor && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                                        <form
                                            onSubmit={onEditTimeHandler}
                                            className="w-full max-w-md space-y-5 rounded-2xl border border-gray-700 bg-[#101010] p-6"
                                        >
                                            <div>
                                                <h2 className="text-xl font-bold text-white">
                                                    Edit Employee Hours
                                                </h2>

                                                <p className="mt-1 text-sm text-gray-400">
                                                    Correct the recorded attendance times.
                                                </p>
                                            </div>

                                            <div>
                                                <label className="mb-2 block text-sm text-gray-300">
                                                    Clock In
                                                </label>

                                                <input
                                                    type="datetime-local"
                                                    value={signInTime}
                                                    onChange={(e) => setSignInTime(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-gray-700 bg-gray-900 p-3 text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="mb-2 block text-sm text-gray-300">
                                                    Clock Out
                                                </label>

                                                <input
                                                    type="datetime-local"
                                                    value={signOutTime}
                                                    onChange={(e) => setSignOutTime(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-gray-700 bg-gray-900 p-3 text-white"
                                                />
                                            </div>

                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOpenHoursEditor(false);
                                                        setEditingSessionId(null);
                                                        setEditingSession(null);
                                                    }}
                                                    className="flex-1 rounded-lg bg-gray-800 px-4 py-3 text-white"
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    type="submit"
                                                    disabled={isUpdateHours}
                                                    className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
                                                >
                                                    {isUpdateHours ? "Saving..." : "Save Changes"}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {openCreateHours && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                                        <form
                                            onSubmit={onCreateHoursHandler}
                                            className="w-full max-w-md space-y-5 rounded-2xl border border-gray-700 bg-[#101010] p-6"
                                        >
                                            <div>
                                                <h2 className="text-xl font-bold text-white">
                                                    Create Employee Hours
                                                </h2>

                                                <p className="mt-1 text-sm text-gray-400">
                                                    Correct the recorded attendance times.
                                                </p>
                                            </div>

                                            <div>
                                                <label className="mb-2 block text-sm text-gray-300">
                                                    Clock In
                                                </label>

                                                <input
                                                    type="datetime-local"
                                                    value={signInTime}
                                                    onChange={(e) => setSignInTime(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-gray-700 bg-gray-600 p-3 text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="mb-2 block text-sm text-gray-300">
                                                    Clock Out
                                                </label>

                                                <input
                                                    type="datetime-local"
                                                    value={signOutTime}
                                                    onChange={(e) => setSignOutTime(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-gray-700 bg-gray-600 p-3 text-white"
                                                />
                                            </div>

                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOpenSession(false);
                                                        setOpenSessionId(null);
                                                        setOpenCreateHours(null);
                                                        setSignInTime("");
                                                        setSignOutTime("");

                                                    }}
                                                    className="flex-1 rounded-lg bg-gray-800 px-4 py-3 text-white"
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    type="submit"
                                                    disabled={isCreateHoursLoading}
                                                    className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
                                                >
                                                    {isCreateHoursLoading ? "Saving..." : "Save Changes"}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* ==================================================
                                SELECTED DAY
                            ================================================== */}
                                {selectedDay && (
                                    <EmployeeTimeSheetHelper.SelectedDayCard
                                        day={selectedDay}
                                        onClose={() =>
                                            setSelectedDate(
                                                null
                                            )
                                        }
                                    />
                                )}

                            </main>
                        </div>

                        <AttendanceChatWindow open={isAttendanceChatOpen} onClose={() => setIsAttendanceChatOpen(false)} />
                    </div>
                </motion.div>
            )
            }
        </main>
    );
}